<template>
  <StatsWidget
    :title="$t('stats_dashboard.pipeline.title')"
    :description="$t('stats_dashboard.pipeline.description')"
    icon="columns"
    tone="sky"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="hasStages"
    @refresh="$emit('refresh')"
  >
    <ul class="stats-pipeline__stages">
      <li
        v-for="stage in stages"
        :key="stage.stage_id"
        class="stats-pipeline__stage"
      >
        <div class="stats-pipeline__stage-head">
          <span class="stats-pipeline__stage-name">{{ stage.stage_name }}</span>
          <span class="stats-pipeline__stage-count">
            {{ formatInteger(stage.count) }}
            <span class="stats-pipeline__stage-count-unit">
              {{ $t('stats_dashboard.pipeline.column_count') }}
            </span>
          </span>
        </div>
        <div class="stats-pipeline__bar">
          <div
            class="stats-pipeline__bar-fill"
            :style="{
              width: stageBarWidth(stage) + '%',
              background: stageGradient(stage),
            }"
          />
        </div>
        <div class="stats-pipeline__stage-foot">
          <span class="stats-pipeline__stage-value">
            {{ formatCurrency(stage.value) }}
          </span>
        </div>
      </li>
    </ul>

    <footer v-if="hasStages" class="stats-pipeline__totals">
      <span class="stats-pipeline__totals-label">
        {{ $t('stats_dashboard.pipeline.total_label') }}
      </span>
      <span class="stats-pipeline__totals-value">
        {{
          $t('stats_dashboard.pipeline.total_format', {
            count: formatInteger(totals.count),
            value: formatCurrency(totals.value),
          })
        }}
      </span>
    </footer>
  </StatsWidget>
</template>

<script>
  /**
   * Pipeline snapshot (§7). No period filter — shows the current state
   * of every open stage. Stages get a horizontal bar sized relative to
   * the stage with the largest *value* in the response, which makes
   * "where is the money sitting" instantly readable.
   */
  import { computed } from 'vue';
  import StatsWidget from './StatsWidget.vue';
  import { formatCurrency, formatInteger } from '@/utils/statsFormat';

  // Color the bars from cool (early stages) to warm (late stages) so
  // the eye picks up flow direction without needing a legend.
  const STAGE_COLORS = [
    ['#cbd5e1', '#94a3b8'], // slate
    ['#bae6fd', '#0284c7'], // sky
    ['#bfdbfe', '#2563eb'], // blue
    ['#c7d2fe', '#4f46e5'], // indigo
    ['#ddd6fe', '#7c3aed'], // violet
    ['#fbcfe8', '#db2777'], // pink
    ['#fecaca', '#dc2626'], // red
    ['#fed7aa', '#ea580c'], // orange
    ['#fde68a', '#d97706'], // amber
    ['#bbf7d0', '#16a34a'], // green
  ];

  export default {
    name: 'StatsPipeline',

    components: { StatsWidget },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const stages = computed(() => props.widget.data?.stages || []);
      const totals = computed(
        () => props.widget.data?.totals || { count: 0, value: 0 }
      );
      const hasStages = computed(
        () =>
          stages.value.length > 0 &&
          stages.value.some((s) => Number(s.count) > 0 || Number(s.value) > 0)
      );

      const maxValue = computed(() => {
        const values = stages.value.map((s) => Number(s.value) || 0);
        return values.length ? Math.max(...values) : 0;
      });

      const stageBarWidth = (stage) => {
        const value = Number(stage.value) || 0;
        if (maxValue.value <= 0) return 2;
        const ratio = value / maxValue.value;
        return Math.max(2, Math.round(ratio * 100));
      };

      const stageGradient = (stage) => {
        const idx = stages.value.findIndex((s) => s.stage_id === stage.stage_id);
        const palette = STAGE_COLORS[idx % STAGE_COLORS.length];
        return `linear-gradient(90deg, ${palette[0]}, ${palette[1]})`;
      };

      return {
        stages,
        totals,
        hasStages,
        stageBarWidth,
        stageGradient,
        formatCurrency,
        formatInteger,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-pipeline {
    &__stages {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
    }

    &__stage {
      display: grid;
      gap: 0.25rem;
    }

    &__stage-head {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.5rem;
    }

    &__stage-name {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-700);
      @include ellipsis(1);
    }

    &__stage-count {
      font-size: 0.75rem;
      color: var(--slate-500);
      font-variant-numeric: tabular-nums;
      flex-shrink: 0;
    }

    &__stage-count-unit {
      color: var(--slate-400);
      margin-left: 0.125rem;
    }

    &__bar {
      height: 10px;
      background: var(--slate-100);
      border-radius: 9999px;
      overflow: hidden;
    }

    &__bar-fill {
      height: 100%;
      border-radius: 9999px;
      transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);
    }

    &__stage-foot {
      display: flex;
      justify-content: flex-end;
    }

    &__stage-value {
      font-size: 0.75rem;
      color: var(--slate-700);
      font-variant-numeric: tabular-nums;
      font-weight: 600;
    }

    &__totals {
      margin-top: 1rem;
      padding-top: 0.75rem;
      border-top: 1px dashed var(--slate-200);
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.5rem;
    }

    &__totals-label {
      font-size: 0.75rem;
      color: var(--slate-500);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      font-weight: 600;
    }

    &__totals-value {
      font-size: 0.875rem;
      color: var(--slate-900);
      font-weight: 700;
      font-variant-numeric: tabular-nums;
    }
  }
</style>
